import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Filter, Plus, Search, Truck, ShieldCheck, BadgeCheck, Zap, ChevronRight, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import AdCard from "@/components/AdCard";
import SearchSuggest from "@/components/SearchSuggest";
import CategorySidebar from "@/components/CategorySidebar";
import BannerInquiryCard from "@/components/BannerInquiryCard";
import PromoCard from "@/components/PromoCard";
import Layout from "@/components/Layout";
import { CATEGORIES, CATEGORY_NAMES, SUBCATEGORY_NAMES, findCategory } from "@/lib/categories";
import { useSEO } from "@/lib/useSEO";
import { trackBeginListing } from "@/lib/analytics";
import SureheldLogo from "@/components/SureheldLogo";

export default function HomePage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = params.category || null;
  const subcategory = params.sub || null;
  const q = searchParams.get("q") || "";
  const fLocation = searchParams.get("location") || "";
  const fPriceMin = searchParams.get("price_min") || "";
  const fPriceMax = searchParams.get("price_max") || "";
  const fMaxAge = searchParams.get("max_age_days") || "";

  const [searchInput, setSearchInput] = useState(q);
  const [ads, setAds] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [error, setError] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/listings/categories");
        if (cancel) return;
        const map = {};
        data.categories.forEach((c) => (map[c.slug] = c.count));
        setCounts(map);
      } catch (e) {}
    })();
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await api.get("/listings", {
          params: {
            category: category || undefined,
            subcategory: subcategory || undefined,
            q: q || undefined,
            location: fLocation || undefined,
            price_min: fPriceMin || undefined,
            price_max: fPriceMax || undefined,
            max_age_days: fMaxAge || undefined,
            page: 1,
            page_size: 30,
          },
        });
        if (cancel) return;
        setAds(data.items);
        setTotal(data.total);
      } catch (e) {
        if (!cancel) setError(apiError(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [category, subcategory, q, fLocation, fPriceMin, fPriceMax, fMaxAge]);

  const onSearch = (e) => {
    e.preventDefault();
    const term = searchInput.trim();
    // When user submits a query, search across ALL categories (Bazoš style).
    // If they were inside a category, navigate to "/" and apply the query there.
    const next = new URLSearchParams();
    if (term) next.set("q", term);
    // Preserve only non-category filters
    ["location", "price_min", "price_max", "max_age_days"].forEach((k) => {
      const v = searchParams.get(k);
      if (v) next.set(k, v);
    });
    if (category || subcategory) {
      navigate({ pathname: "/", search: next.toString() ? `?${next.toString()}` : "" });
    } else {
      setSearchParams(next);
    }
  };

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, String(value));
    else next.delete(key);
    setSearchParams(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    ["location", "price_min", "price_max", "max_age_days"].forEach((k) => next.delete(k));
    setSearchParams(next);
  };

  const hasActiveFilters = !!(fLocation || fPriceMin || fPriceMax || fMaxAge);
  const activeCategory = useMemo(() => findCategory(category), [category]);
  const heroVisible = !category && !subcategory && !q && !hasActiveFilters;

  const seoTitle = category
    ? `${CATEGORY_NAMES[category] || ""}${subcategory ? ` – ${SUBCATEGORY_NAMES[subcategory]}` : ""} – bazar`
    : q
      ? `Hledání: ${q}`
      : null;
  const seoDesc = category
    ? `${CATEGORY_NAMES[category]} – stavební inzerce a bazar na STAVBOŠ. Online inzerce zdarma, TOP za 49 Kč.`
    : undefined;
  useSEO({ title: seoTitle, description: seoDesc });

  return (
    <Layout>
      {heroVisible && <Hero onSearchClick={() => document.getElementById("search-input")?.focus()} />}

      <section className="mt-6">
        <div className="grid md:grid-cols-4 gap-6">
          <div className="md:col-span-1 order-2 md:order-1">
            <CategorySidebar activeSlug={category} activeSub={subcategory} counts={counts} />
            <div className="hidden md:block mt-6">
              <BannerInquiryCard />
            </div>
            <div className="hidden md:block mt-6">
              <PromoCard />
            </div>
          </div>

          <div className="md:col-span-3 order-1 md:order-2 flex flex-col gap-4">
            <form
              onSubmit={onSearch}
              className="stb-card p-3 flex flex-col sm:flex-row gap-2 items-stretch"
              data-testid="search-form"
            >
              <SearchSuggest value={searchInput} onChange={setSearchInput} />
              <button type="submit" className="stb-btn stb-btn-primary" data-testid="search-submit">
                Hledat
              </button>
              <button
                type="button"
                onClick={() => setShowFilters((s) => !s)}
                className={`stb-btn ${showFilters || hasActiveFilters ? "stb-btn-primary" : "stb-btn-secondary"}`}
                data-testid="filters-toggle"
              >
                <Filter size={16} /> Filtr
                {hasActiveFilters && (
                  <span className="ml-1 stb-mono text-xs">
                    ({[fLocation, fPriceMin || fPriceMax, fMaxAge].filter(Boolean).length})
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  trackBeginListing({ source: "home_search_bar" });
                  navigate("/novy-inzerat");
                }}
                className="stb-btn stb-btn-secondary"
                data-testid="hero-new-ad"
              >
                <Plus size={16} /> Vložit
              </button>
            </form>

            {showFilters && (
              <div className="stb-card p-3" data-testid="filters-panel">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="stb-label">Filtr inzerátů</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="stb-btn stb-btn-ghost text-xs"
                      data-testid="filters-clear"
                    >
                      <X size={12} /> Smazat filtr
                    </button>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
                  <FilterField label="Lokalita">
                    <input
                      className="stb-input"
                      placeholder="Praha, Brno…"
                      defaultValue={fLocation}
                      onBlur={(e) => updateFilter("location", e.target.value.trim())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          updateFilter("location", e.target.value.trim());
                        }
                      }}
                      data-testid="filter-location"
                    />
                  </FilterField>
                  <FilterField label="Cena od (Kč)">
                    <input
                      className="stb-input"
                      type="number"
                      min={0}
                      placeholder="od"
                      defaultValue={fPriceMin}
                      onBlur={(e) => updateFilter("price_min", e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          updateFilter("price_min", e.target.value);
                        }
                      }}
                      data-testid="filter-price-min"
                    />
                  </FilterField>
                  <FilterField label="Cena do (Kč)">
                    <input
                      className="stb-input"
                      type="number"
                      min={0}
                      placeholder="do"
                      defaultValue={fPriceMax}
                      onBlur={(e) => updateFilter("price_max", e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          updateFilter("price_max", e.target.value);
                        }
                      }}
                      data-testid="filter-price-max"
                    />
                  </FilterField>
                  <FilterField label="Stáří inzerátu">
                    <select
                      className="stb-input"
                      value={fMaxAge}
                      onChange={(e) => updateFilter("max_age_days", e.target.value)}
                      data-testid="filter-age"
                    >
                      <option value="">Vše</option>
                      <option value="1">Posledních 24 h</option>
                      <option value="7">Posledních 7 dní</option>
                      <option value="30">Posledních 30 dní</option>
                      <option value="90">Posledních 90 dní</option>
                    </select>
                  </FilterField>
                </div>
              </div>
            )}

            <Breadcrumbs category={category} subcategory={subcategory} q={q} />

            <div className="flex items-center justify-between">
              <h2 className="stb-h text-2xl text-[#0E0E0E]">
                {category
                  ? `${CATEGORY_NAMES[category] || "Inzeráty"}${
                      subcategory ? ` / ${SUBCATEGORY_NAMES[subcategory] || ""}` : ""
                    }`
                  : q
                    ? `Hledání: "${q}"`
                    : "Nejnovější inzeráty"}
              </h2>
              <span className="stb-mono text-xs text-[#0E0E0E]">{total} ks</span>
            </div>

            {error && (
              <div className="stb-card p-3 text-[#FF6B00] border-[#FF6B00]" data-testid="ads-error">
                {error}
              </div>
            )}

            {loading ? (
              <LoadingList />
            ) : ads.length === 0 ? (
              <EmptyState category={activeCategory} />
            ) : (
              <div className="flex flex-col gap-3" data-testid="ads-list">
                {ads.map((a) => (
                  <AdCard key={a.id} ad={a} />
                ))}
              </div>
            )}

            <div className="md:hidden">
              <BannerInquiryCard />
            </div>
            <div className="md:hidden mt-4">
              <PromoCard />
            </div>
          </div>
        </div>
      </section>

      {heroVisible && <ValueProps />}
    </Layout>
  );
}

function Hero({ onSearchClick }) {
  return (
    <section className="stb-card p-6 md:p-10 mb-6 relative overflow-hidden" data-testid="home-hero">
      <span className="stb-rivet" style={{ top: 8, left: 8 }} />
      <span className="stb-rivet" style={{ top: 8, right: 8 }} />
      <span className="stb-rivet" style={{ bottom: 8, left: 8 }} />
      <span className="stb-rivet" style={{ bottom: 8, right: 8 }} />
      <div className="grid md:grid-cols-[1fr_1.15fr] gap-6 items-start">
        <div>
          <p className="text-white text-sm">
            Stavební bazar a online inzerce. Nářadí, stroj nebo míchačka? Najít zde je hračka…
          </p>
          <img
            src="/stavbos-logo-wide.png"
            alt="STAVBOŠ"
            className="block h-16 md:h-24 w-auto mt-3"
          />
          <h1 className="stb-h text-3xl sm:text-4xl lg:text-5xl text-white mt-4 leading-[0.95]">
            STROJE – NÁŘADÍ<br />MATERIÁLY – PŮJČOVNA
          </h1>
          <p className="text-white mt-4 max-w-md">
            Inzerce bez pravidel, kde můžete vložit vše bez buzerace.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 auto-rows-fr">
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={`/kategorie/${c.slug}`}
              className="stb-card px-4 py-4 hover:border-[#FF6B00] flex items-center justify-between gap-2 no-underline min-h-[72px]"
              data-testid={`hero-cat-${c.slug}`}
            >
              <span className="stb-h text-base md:text-lg tracking-tight text-white leading-[1.1]">
                {c.heroLabel || c.name}
              </span>
              <ChevronRight size={18} className="text-[#FF6B00] shrink-0 stb-glow-orange" />
            </Link>
          ))}
        </div>
      </div>
      {/* Action row uses the SAME grid as above so buttons align with the left
          text column and Dražby věcí aligns with the right category column. */}
      <div className="grid md:grid-cols-[1fr_1.15fr] gap-6 mt-6">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/novy-inzerat"
            className="stb-btn stb-btn-primary"
            data-testid="hero-add-ad"
            onClick={() => trackBeginListing({ source: "home_hero" })}
          >
            <Plus size={16} /> Vložit inzerát
          </Link>
          <button onClick={onSearchClick} className="stb-btn stb-btn-secondary" data-testid="hero-browse">
            <Search size={16} /> Prohlížet inzeráty
          </button>
        </div>
        <div className="inline-flex items-center gap-4 self-center justify-self-end flex-wrap">
          <span
            className="stb-label text-[#FF6B00] whitespace-nowrap"
            data-testid="hero-drazbyveci-kicker"
          >
            PARTNERSKÉ WEBY
          </span>
          <a
            href="https://www.sureheld.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center group no-underline"
            data-testid="hero-sureheld-link"
            title="Otevřít Sureheld v novém okně"
          >
            <SureheldLogo
              className="transition-transform duration-200 group-hover:scale-105"
              testId="hero-sureheld-logo"
            />
          </a>
          <a
            href="https://drazbyveci.cz/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 group no-underline"
            data-testid="hero-drazbyveci-link"
            title="Otevřít Dražby věcí v novém okně"
          >
            <img
              src="/drazbyveci-logo.png"
              alt="Dražby věcí"
              className="h-14 w-14 rounded-full bg-white object-contain transition-transform duration-200 group-hover:scale-105"
              data-testid="hero-drazbyveci-logo"
            />
            <span className="text-white text-sm leading-tight group-hover:text-[#FF6B00] transition-colors">
              Dražby věcí<br />inzertní aukce zdarma
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="stb-label text-[#A3A3A3]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Breadcrumbs({ category, subcategory, q }) {
  if (!category && !q) return null;
  return (
    <nav className="text-xs stb-mono text-[#0E0E0E]" data-testid="breadcrumbs">
      <Link to="/" className="hover:underline">Domů</Link>
      {category && (
        <>
          {" / "}
          <Link to={`/kategorie/${category}`} className="hover:underline">
            {CATEGORY_NAMES[category]}
          </Link>
        </>
      )}
      {subcategory && (
        <>
          {" / "}
          <span>{SUBCATEGORY_NAMES[subcategory]}</span>
        </>
      )}
      {q && (
        <>
          {" / "}
          <span>"{q}"</span>
        </>
      )}
    </nav>
  );
}

function LoadingList() {
  return (
    <div className="flex flex-col gap-3" data-testid="ads-loading">
      {[0, 1, 2].map((i) => (
        <div key={i} className="stb-card h-32 animate-pulse" />
      ))}
    </div>
  );
}

function EmptyState({ category }) {
  return (
    <div className="stb-card p-8 text-center" data-testid="ads-empty">
      <span className="stb-stripes block h-2 mb-4 mx-auto w-24" />
      <h3 className="stb-h text-2xl text-white">Ještě tu nic není</h3>
      <p className="text-[#A3A3A3] mt-2">
        {category
          ? `V kategorii "${category.name}" nejsou žádné inzeráty.`
          : "Buď první a vlož svůj inzerát."}
      </p>
      <Link
        to="/novy-inzerat"
        className="stb-btn stb-btn-primary mt-4 inline-flex"
        onClick={() => trackBeginListing({ source: "home_empty" })}
      >
        <Plus size={16} /> Vložit inzerát
      </Link>
    </div>
  );
}

function ValueProps() {
  const items = [
    {
      icon: Truck,
      title: "Vše kolem staveb",
      text: "Stavební bazar: stroje, nářadí, materiál, interiéry, zahrada i půjčovna. Ne obecný vetešnictví.",
    },
    {
      icon: ShieldCheck,
      title: "Ověřený email",
      text: "Každý inzerent ověří kontakt — méně podvodníků, víc reálných obchodů.",
    },
    {
      icon: Zap,
      title: "TOP za 49 Kč",
      text: "Stačí topnout a posuneš se nad ostatní. Přebíjej konkurenci.",
    },
    {
      icon: BadgeCheck,
      title: "Foto prodejce",
      text: "Nepovinné, ale výrazně zvyšuje důvěryhodnost.",
    },
  ];
  return (
    <section className="mt-10" data-testid="value-props">
      <h2 className="stb-h text-3xl text-[#0E0E0E] mb-4">Proč Stavboš?</h2>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it, i) => (
          <div key={i} className="stb-card p-4 relative">
            <span className="stb-rivet" style={{ top: 4, left: 4 }} />
            <span className="stb-rivet" style={{ top: 4, right: 4 }} />
            <it.icon size={26} className="text-[#FF6B00] mb-3" />
            <h3 className="stb-h text-lg text-white">{it.title}</h3>
            <p className="text-sm text-[#A3A3A3] mt-1">{it.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
